import React from 'react';

const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header - Same as landing page */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-lg border-b border-slate-900/10">
        <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
          <a href="/" className="flex items-center space-x-2 text-2xl font-bold text-slate-800">
            <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <span>ThreadIt</span>
          </a>

          <div className="hidden md:flex items-center space-x-8">
            <a href="/" className="text-slate-600 hover:text-emerald-500 transition-colors">Home</a>
            <a href="/pricing" className="text-slate-600 hover:text-emerald-500 transition-colors">Pricing</a>
            <a href="/contact" className="text-slate-600 hover:text-emerald-500 transition-colors">Contact</a>
          </div>

          <div className="hidden md:block">
            <a 
              href="/app" 
              className="inline-block text-center px-6 py-2 rounded-lg font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-100 bg-emerald-500 text-white hover:bg-emerald-600 focus:ring-emerald-500 shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40"
            >
              Login
            </a>
          </div>

          <div className="md:hidden">
            <button aria-label="Toggle menu" className="text-slate-800">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Terms of Service</h1>
          <p className="text-slate-600 mb-8">Last Updated: August 21, 2025</p>
          
          <div className="bg-white rounded-lg shadow-md p-8 space-y-8">
            {/* Agreement to Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">1. Agreement to Terms</h2>
              <p className="text-slate-700 leading-relaxed">
                By accessing or using our website, www.threadit.ai (the "Service"), you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use the Service.
              </p>
            </section>

            {/* User Submissions */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">2. User Submissions</h2>
              <p className="text-slate-700 leading-relaxed">
                When you submit content to the Service, such as information about a new security tool, you are solely responsible for the accuracy and legality of that content. By submitting content, you grant ThreadIt a worldwide, non-exclusive, royalty-free license to use, reproduce, and display that content in connection with the Service.
              </p>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">3. Intellectual Property</h2>
              <p className="text-slate-700 leading-relaxed">
                The ThreadIt brand, logo, and all original content, features, and functionality on the Service are the exclusive property of the site owners and are protected by international copyright and trademark laws.
              </p>
            </section>

            {/* Disclaimer of Warranties */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">4. Disclaimer of Warranties</h2>
              <p className="text-slate-700 leading-relaxed">
                The Service is provided on an "as is" and "as available" basis. ThreadIt makes no warranties, express or implied, regarding the accuracy, completeness, or reliability of the conversational data, tool information, or any other content on the Service. We expressly disclaim any warranties regarding the security, effectiveness, or performance of the tools listed on our platform. Your use of the Service and reliance on its content is at your own risk.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">5. Limitation of Liability</h2>
              <p className="text-slate-700 leading-relaxed">
                In no event shall ThreadIt, its owners, or its affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Service.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">6. Governing Law</h2>
              <p className="text-slate-700 leading-relaxed">
                These Terms shall be governed and construed in accordance with the laws of Germany, without regard to its conflict of law provisions.
              </p>
            </section>

            {/* Contact Us */}
            <section>
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">7. Contact Us</h2>
              <p className="text-slate-700 mb-2">If you have any questions about these Terms, please contact us at:</p>
              <p className="text-slate-700 font-medium">hello@threadit.ai</p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 border-t border-slate-200 mt-16">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-8 mb-4 md:mb-0">
              <div className="flex items-center space-x-2 text-xl font-bold text-slate-800">
                <svg className="w-7 h-7 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span>ThreadIt</span>
              </div>
              <div className="flex space-x-6 text-sm text-slate-500">
                <a href="/privacy" className="hover:text-emerald-500 transition-colors">Privacy Policy</a>
                <a href="/terms" className="hover:text-emerald-500 transition-colors">Terms of Service</a>
                <a href="/impressum" className="hover:text-emerald-500 transition-colors">Impressum</a>
              </div>
            </div>
            <div className="flex space-x-6 text-slate-500">
              <a href="/#features" className="hover:text-emerald-500 transition-colors">Features</a>
              <a href="/pricing" className="hover:text-emerald-500 transition-colors">Pricing</a>
              <a href="/contact" className="hover:text-emerald-500 transition-colors">Contact</a>
            </div>
          </div>
          <div className="mt-8 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} ThreadIt Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
